// Rechter paneel — alles wat met analyse en context van het scenario te
// maken heeft:
//   - Logo (branding bovenaan)
//   - Analyse-acties: bereken jaarschaduw + heatmap aan/uit
//   - Een selectie-header (welk blok is geselecteerd) als context voor de
//     statistieken eronder
//   - Statistieken: óf scenario-meta (geen blok geselecteerd) óf
//     meetvlak-statistieken (bij tuin/bestrating/gebouw-met-heightTop)
//
// Bewerk-acties zelf (naam/type/hoogte/kleur/etc.) staan links naast de
// bloklijst — zie ScenarioPanel + BlockProperties.

import { Calculator, Flame, RefreshCcw } from "lucide-react";
import { useStore } from "../state/store";
import {
  BLOCK_TYPE_DEFAULTS,
  type Block,
} from "../types/scenario";
import { hashScenarioForShadow } from "../shadow";
import { Button } from "./ui/Button";
import { Field } from "./ui/Field";
import { ResultsPanel } from "./ResultsPanel";

export function BlockPanel() {
  const block = useStore((s) =>
    s.selectedBlockId ? s.scenario.blocks.find((b) => b.id === s.selectedBlockId) ?? null : null,
  );

  return (
    <aside className="w-72 border-l border-border bg-card flex flex-col overflow-y-auto">
      <LogoSection />
      <AnalysisActions />
      {block ? <SelectedBlockHeader block={block} /> : null}

      <div className="p-4">
        {block ? <BlockResults block={block} /> : <ScenarioMetaPanel />}
      </div>
    </aside>
  );
}

function LogoSection() {
  const logoUrl = useStore((s) => s.logoUrl);
  if (!logoUrl) return null;
  return (
    <section className="px-3 py-3 border-b border-border flex items-center justify-center bg-secondary/30">
      <img
        src={logoUrl}
        alt="Logo"
        className="max-h-12 max-w-full object-contain"
        onError={(e) => {
          // Verberg gebroken plaatjes stil — de admin moet 'm dan opnieuw zetten.
          (e.currentTarget as HTMLImageElement).style.display = "none";
        }}
      />
    </section>
  );
}

function AnalysisActions() {
  const yearJob = useStore((s) => s.yearJob);
  const yearResult = useStore((s) => s.yearResult);
  const yearResultHash = useStore((s) => s.yearResultHash);
  const scenario = useStore((s) => s.scenario);
  const startYearComputation = useStore((s) => s.startYearComputation);
  const heatmapVisible = useStore((s) => s.heatmapVisible);
  const setHeatmapVisible = useStore((s) => s.setHeatmapVisible);

  const currentHash = hashScenarioForShadow(scenario);
  const stale = yearResult !== null && yearResultHash !== null && currentHash !== yearResultHash;
  const hasResult = yearResult !== null;
  const hasMeasurementSurface = scenario.blocks.some(
    (b) => b.type === "tuin" || b.type === "bestrating" || b.heightTop > 0,
  );

  return (
    <section className="p-3 border-b border-border space-y-2">
      <h2 className="text-xs font-semibold uppercase text-muted-foreground">
        Analyse
      </h2>

      <Button
        size="sm"
        className="w-full"
        onClick={() => startYearComputation()}
        disabled={yearJob.running || !hasMeasurementSurface}
        title={
          !hasMeasurementSurface
            ? "Voeg eerst een tuin, bestrating of gebouw toe als meetvlak"
            : stale
              ? "Geometrie gewijzigd — herbereken"
              : hasResult
                ? "Resultaten zijn actueel"
                : "Bereken jaarschaduw"
        }
      >
        {stale ? <RefreshCcw size={14} /> : <Calculator size={14} />}
        {yearJob.running
          ? `Berekent… ${yearJob.pct.toFixed(0)}%`
          : stale
            ? "Herbereken jaarschaduw"
            : "Bereken jaarschaduw"}
      </Button>

      <Button
        variant={heatmapVisible ? "active" : "outline"}
        size="sm"
        className="w-full"
        onClick={() => setHeatmapVisible(!heatmapVisible)}
        disabled={!hasResult}
        title={hasResult ? "Heatmap aan/uit" : "Eerst jaarschaduw berekenen"}
      >
        <Flame size={14} /> Heatmap
      </Button>

      {!hasMeasurementSurface && (
        <p className="text-[11px] text-muted-foreground leading-snug">
          Voeg een tuin, bestrating of gebouw toe om een meetvlak te krijgen
          waarvoor de jaarschaduw berekend kan worden.
        </p>
      )}
    </section>
  );
}

function SelectedBlockHeader({ block }: { block: Block }) {
  const def = BLOCK_TYPE_DEFAULTS[block.type];
  const heightLabel = block.heightTop > 0 ? ` · ${block.heightTop} m` : "";
  return (
    <section className="px-4 py-3 border-b border-border bg-secondary/30">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
        Geselecteerd
      </div>
      <div className="flex items-center gap-2 min-w-0">
        <span
          className="w-4 h-4 rounded-sm border border-black/20 flex-none"
          style={{ backgroundColor: block.color }}
          title={block.color}
        />
        <span className="font-medium text-sm truncate" title={block.name}>
          {block.name}
        </span>
      </div>
      <div className="text-xs text-muted-foreground mt-0.5">
        {def.label}
        {heightLabel}
      </div>
    </section>
  );
}

function BlockResults({ block }: { block: Block }) {
  if (block.type === "tuin" || block.type === "bestrating") {
    return <ResultsPanel surfaceId={`${block.id}:ground`} />;
  }
  if (block.type === "gebouw" && block.heightTop === 0) {
    return <ResultsPanel surfaceId={`${block.id}:ground`} />;
  }
  if (block.type === "gebouw" && block.heightTop > 0) {
    return <ResultsPanel surfaceId={`${block.id}:roof`} />;
  }
  // Boomstam / boombladdek / overige: geen meetvlak, dus geen statistieken.
  return (
    <p className="text-xs text-muted-foreground">
      Dit type blok is geen meetvlak — er zijn geen jaarstatistieken voor.
      Selecteer een tuin, bestrating of gebouw voor de schaduw-uitkomsten.
    </p>
  );
}

function ScenarioMetaPanel() {
  const scenario = useStore((s) => s.scenario);
  const setName = useStore((s) => s.setScenarioName);
  const setDescription = useStore((s) => s.setScenarioDescription);
  const setAuthor = useStore((s) => s.setScenarioAuthor);

  const created = formatDate(scenario.createdAt);
  const updated = formatDate(scenario.updatedAt);
  const blockCount = scenario.blocks.length;

  return (
    <>
      <h2 className="text-sm font-semibold mb-3">Scenario-eigenschappen</h2>
      <p className="text-xs text-muted-foreground mb-3">
        Selecteer een blok in de bloklijst of op de kaart om de bloklevenscyclus
        aan te passen. Hieronder staan de eigenschappen van het hele scenario.
      </p>

      <Field label="Naam">
        <input
          value={scenario.name}
          onChange={(e) => setName(e.target.value)}
          className="w-full bg-background border border-input rounded-md px-2 py-1 text-sm"
        />
      </Field>

      <Field label="Beschrijving">
        <textarea
          value={scenario.description ?? ""}
          onChange={(e) => setDescription(e.target.value)}
          rows={4}
          placeholder="Bv. 'Voorgenomen bouw oostzijde, 5 verdiepingen'"
          className="w-full bg-background border border-input rounded-md px-2 py-1 text-sm resize-y"
        />
      </Field>

      <Field
        label="Auteur"
        hint="Naam of pseudoniem dat zichtbaar is bij publieke scenario's."
      >
        <input
          value={scenario.author ?? ""}
          onChange={(e) => setAuthor(e.target.value)}
          className="w-full bg-background border border-input rounded-md px-2 py-1 text-sm"
        />
      </Field>

      <section className="text-xs text-muted-foreground space-y-1 pt-3 border-t border-border">
        <div className="flex justify-between">
          <span>Aangemaakt</span>
          <span className="font-mono">{created}</span>
        </div>
        <div className="flex justify-between">
          <span>Laatst bewerkt</span>
          <span className="font-mono">{updated}</span>
        </div>
        <div className="flex justify-between">
          <span>Aantal blokken</span>
          <span className="font-mono">{blockCount}</span>
        </div>
        <div className="flex justify-between">
          <span>Zichtbaarheid</span>
          <span className="font-mono">
            {scenario.isPublic ? "publiek" : "persoonlijk"}
          </span>
        </div>
      </section>
    </>
  );
}

function formatDate(iso: string | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("nl-NL", {
    dateStyle: "short",
    timeStyle: "short",
  });
}
